import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

const AudioContext = createContext(null);

const MUSIC_URL =
  "https://customer-assets.emergentagent.com/job_eab8ba41-4926-4e2a-a9ee-abba0f452021/artifacts/hpeqwnzi_Married%20Life.mp3";

export function AudioProvider({ children }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const audio = new Audio(MUSIC_URL);
    audio.loop = true;
    audio.volume = 0.45;
    audio.preload = "auto";
    audioRef.current = audio;
    const onCanPlay = () => setReady(true);
    audio.addEventListener("canplaythrough", onCanPlay);

    return () => {
      audio.removeEventListener("canplaythrough", onCanPlay);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const start = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      setIsPlaying(true);
    } catch (err) {
      // Most likely blocked by browser autoplay policy — user gesture required
      console.debug("[Audio] auto-start failed:", err);
    }
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        await a.play();
        setIsPlaying(true);
      } catch (err) {
        console.debug("[Audio] play failed:", err);
      }
    } else {
      a.pause();
      setIsPlaying(false);
    }
  };

  const value = useMemo(
    () => ({ isPlaying, toggle, start, ready }),
    // toggle/start are stable enough — they only read the ref and call setState
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPlaying, ready]
  );

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioContext);
  if (!ctx) throw new Error("useAudio must be used inside AudioProvider");
  return ctx;
}
